import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type ContactOption = {
  key: string;
  icon: string;
  href: string;
};

export default function Buttonizer() {
  const { t } = useTranslation("home");
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const phoneNumber = t("buttonizer.phone_number", "+4921154419380");
  const emailAddress = t("buttonizer.email_address", "contact@net-basis.com");

  const contactOptions: ContactOption[] = [
    {
      key: "phone",
      icon: "ri-phone-line",
      href: `tel:${phoneNumber}`,
    },
    {
      key: "email",
      icon: "ri-mail-line",
      href: `mailto:${emailAddress}`,
    },
    {
      key: "whatsapp",
      icon: "ri-whatsapp-line",
      href: t("buttonizer.whatsapp_link", "https://wa.me/4915126386361"),
    },
    {
      key: "telegram",
      icon: "ri-telegram-line",
      href: t("buttonizer.telegram_link", "https://t.me/ChriGue1"),
    },
    {
      key: "signal",
      icon: "fa-brands fa-signal-messenger",
      href: t("buttonizer.signal_link", "https://signal.me/#p/+4915126386361"),
    },
    {
      key: "messenger",
      icon: "ri-messenger-line",
      href: t("buttonizer.messenger_link", "https://m.me/ChriGue1"),
    },
  ];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Contact Panel */}
      <div
        ref={panelRef}
        className={`fixed bottom-24 right-6 md:right-8 z-50 flex flex-col gap-2 transition-all duration-300 origin-bottom-right ${
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-90 translate-y-4 pointer-events-none"
        }`}
      >
        {contactOptions.map((option, index) => (
          <a
            key={option.key}
            href={option.href}
            target={option.key !== "phone" && option.key !== "email" ? "_blank" : undefined}
            rel={option.key !== "phone" && option.key !== "email" ? "noopener noreferrer" : undefined}
            className="flex items-center gap-3 bg-white rounded-full pl-4 pr-5 py-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 cursor-pointer group"
            style={{
              animationDelay: open ? `${index * 50}ms` : "0ms",
              animation: open ? "buttonizerSlideUp 0.35s ease-out both" : "none",
            }}
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-white group-hover:bg-primary-600 transition-colors">
              <i className={`${option.icon} text-lg`} />
            </span>
            <span className="font-heading text-sm font-semibold text-foreground-800 whitespace-nowrap">
              {t(`buttonizer.${option.key}_label`, option.key)}
            </span>
          </a>
        ))}
      </div>

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 md:right-8 z-50 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${
          open ? "bg-primary-600" : ""
        }`}
        aria-label={t("buttonizer.trigger_label", "Contact")}
      >
        <span className="w-6 h-6 flex items-center justify-center">
          <span className="relative inline-flex items-center justify-center w-[18px] h-[14px] bg-white rounded-[3px]">
            <span className="flex gap-[2px]">
              <span className="w-[3px] h-[3px] bg-primary-500 rounded-full" />
              <span className="w-[3px] h-[3px] bg-primary-500 rounded-full" />
              <span className="w-[3px] h-[3px] bg-primary-500 rounded-full" />
            </span>
            <span className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-t-[4px] border-t-white" />
          </span>
        </span>
        <span className="font-heading text-sm font-semibold whitespace-nowrap">
          {t("buttonizer.trigger_label", "Contact")}
        </span>
      </button>
    </>
  );
}